import React from 'react';
import { Artifact } from '../../../types/artifact';
import { useTheme } from '../../../contexts/ThemeContext';
import MermaidBlock from '../../conversation/MermaidBlock';

interface MermaidDiagramViewerProps {
  artifact: Artifact;
  mode?: string;
}

// 2026-05-16: deduplicated against the chat-side renderer. The previous
// implementation maintained its own mermaid.render call with no setTimeout
// guard, no cancellation flag, no error cleanup, and no shared sanitizer
// — different failure mode from the chat path on identical content
// (operators saw `TypeError: Cannot read properties of null (reading
// 'firstChild')` from mermaid internals). Delegating to MermaidBlock
// gets all of:
//   - sanitizeMermaidCode coercions (single-% → %%, \" unescape, ""]
//     collapse, paren-wrap cylinder strip)
//   - setTimeout(doRender, 300) settle window
//   - ref-mount guard + async cancellation flag
//   - cleanupMermaidErrors() pre/post render
//   - fullscreen zoom modal + copy/download chrome
// MermaidBlock requires a `children` slot for the pre/code fallback when
// mermaid rejects; we pass the raw source so the operator sees what
// failed.

export const MermaidDiagramViewer: React.FC<MermaidDiagramViewerProps> = ({
  artifact,
}) => {
  const { darkMode } = useTheme();

  const rawCode: string =
    typeof artifact.content === 'string'
      ? artifact.content
      : (artifact.content?.mermaid_code || '');

  const description: string | undefined =
    typeof artifact.content === 'object' && artifact.content?.description
      ? artifact.content.description
      : undefined;

  if (!rawCode) {
    return (
      <div className={`p-4 border rounded text-sm ${darkMode ? 'border-amber-700 bg-amber-900/30 text-amber-300' : 'border-amber-300 bg-amber-50 text-amber-700'}`}>
        Mermaid artifact has no <code>mermaid_code</code> content.
      </div>
    );
  }

  return (
    <div className="mermaid-diagram-viewer">
      {description && (
        <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {description}
        </p>
      )}
      <MermaidBlock code={rawCode} darkMode={darkMode}>
        <pre><code>{rawCode}</code></pre>
      </MermaidBlock>
    </div>
  );
};
